import { Box, Stack } from '@mui/joy';
import { Info } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { ExpandableList } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextLine, Tooltip } from 'core/elements';
import { IExternalProjectAccount } from 'projects/types';
import { getActiveExternalProjectAccounts } from 'projects/utils';
import { Direction } from 'theme/enums';

const MAX_DISPLAYED_ITEMS = 2;

interface IItemProps {
  account: IExternalProjectAccount;
  isActive: boolean;
}

const ExternalCustomerItem = ({ account, isActive }: IItemProps) => {
  const { t } = useTranslation();

  if (isActive) {
    return (
      <>
        <TextLine text={account.customerName} />

        <TextLine text={account.accountNumber} isSubText />
      </>
    );
  }

  return (
    <>
      <Box sx={{ textDecoration: 'line-through' }}>
        <TextLine text={account.customerName} />
      </Box>

      <Stack direction={Direction.ROW} alignItems="center" gap={0.5}>
        <Box sx={{ textDecoration: 'line-through' }}>
          <TextLine text={account.accountNumber} isSubText />
        </Box>

        <Tooltip title={t('tooltipInactiveAccountLink')} childrenSx={{ cursor: 'default' }}>
          <Info
            data-testid="inactive-account-info"
            tabIndex={0}
            aria-label={t('tooltipInactiveAccountLink')}
          />
        </Tooltip>
      </Stack>
    </>
  );
};

interface IProps extends ICellRendererParams {
  projectId: number;
}

export const ExternalCustomersCell = ({ value, projectId }: IProps) => {
  if (!value || !value.length) {
    return DEFAULT_PLACEHOLDER;
  }

  const accounts = value as IExternalProjectAccount[];

  // An account is listed once per association type; it is inactive only when none of its
  // links is active.
  const activeAccountNumbers = new Set(
    getActiveExternalProjectAccounts(accounts).map((account) => account.accountNumber)
  );

  return (
    <ExpandableList<IExternalProjectAccount>
      items={accounts}
      itemRenderer={(account: IExternalProjectAccount) => (
        <ExternalCustomerItem
          account={account}
          isActive={activeAccountNumbers.has(account.accountNumber)}
        />
      )}
      getItemKey={(account: IExternalProjectAccount) =>
        `${projectId}${accounts.indexOf(account)}${account.accountNumber}`
      }
      maxItems={MAX_DISPLAYED_ITEMS}
    />
  );
};
